// Normalizacao tolerante do design salvo (jsonb vindo do banco ou do painel).
//
// Nunca lanca: campo invalido vira o valor do modelo (ou um padrao seguro),
// caixa e sempre clampada para dentro da tela, textos sao cortados e cores so
// aceitam #rrggbb. O renderizador confia no resultado sem revalidar.
import { presetDesign, findWatermarkPreset, DEFAULT_WATERMARK_PRESET } from "./presets.js";
import { BAND_SIZE_MAX, BAND_SIZE_MIN, MAX_ROTATION, MAX_WATERMARK_LAYERS, MAX_WATERMARK_TEXT, MIN_LAYER_SIZE, MIN_LOGO_OPACITY, WATERMARK_FLOWS, } from "./types.js";
const HEX_COLOR_RE = /^#[0-9a-f]{6}$/i;
const LAYER_ID_RE = /^[a-z0-9_-]{1,24}$/;
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u0009\u000b-\u001f\u007f]/g;
const BADGE_SHAPES = ["circle", "pill", "ribbon"];
const ALIGNS = ["left", "center", "right"];
function asRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? value : {};
}
function num(value, fallback) {
    return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
export function isHexColor(value) {
    return typeof value === "string" && HEX_COLOR_RE.test(value);
}
function color(value, fallback) {
    return isHexColor(value) ? value.toLowerCase() : fallback;
}
function optionalColor(value, fallback) {
    if (value === null)
        return null;
    return isHexColor(value) ? value.toLowerCase() : fallback;
}
/** Texto limpo: sem caracteres de controle (exceto quebra de linha), cortado. */
export function cleanText(value, fallback) {
    if (typeof value !== "string")
        return fallback;
    return value.replace(CONTROL_RE, "").slice(0, MAX_WATERMARK_TEXT).trim();
}
function oneOf(value, allowed, fallback) {
    return allowed.includes(value) ? value : fallback;
}
/** Caixa sempre dentro da tela: lado >= MIN_LAYER_SIZE e x+w <= 1, y+h <= 1. */
export function normalizeBox(raw, fallback) {
    const r = asRecord(raw);
    const w = clamp(num(r.w, fallback.w), MIN_LAYER_SIZE, 1);
    const h = clamp(num(r.h, fallback.h), MIN_LAYER_SIZE, 1);
    return {
        x: clamp(num(r.x, fallback.x), 0, 1 - w),
        y: clamp(num(r.y, fallback.y), 0, 1 - h),
        w,
        h,
    };
}
function normalizeBand(raw, fallback) {
    const r = asRecord(raw);
    return {
        enabled: typeof r.enabled === "boolean" ? r.enabled : fallback.enabled,
        color: color(r.color, fallback.color),
        text: cleanText(r.text, fallback.text),
        textColor: color(r.textColor, fallback.textColor),
        size: clamp(num(r.size, fallback.size), BAND_SIZE_MIN, BAND_SIZE_MAX),
    };
}
function normalizePhoto(raw, fallback) {
    const r = asRecord(raw);
    return {
        background: color(r.background, fallback.background),
        fit: oneOf(r.fit, ["contain", "cover"], fallback.fit),
    };
}
const DEFAULT_BOX = { x: 0.3, y: 0.4, w: 0.4, h: 0.12 };
/** Camada nova de um tipo, com valores padrao (usada pelo editor do painel). */
export function createWatermarkLayer(kind, id) {
    const base = { id, box: DEFAULT_BOX, rotation: 0 };
    switch (kind) {
        case "text":
            return {
                ...base,
                kind,
                text: "Seu texto",
                color: "#ffffff",
                background: "#111827",
                align: "center",
                bold: true,
            };
        case "badge":
            return {
                ...base,
                kind,
                box: { x: 0.7, y: 0.06, w: 0.24, h: 0.24 },
                text: "-{discountPercent}",
                color: "#dc2626",
                textColor: "#ffffff",
                shape: "circle",
            };
        case "contacts":
            return {
                ...base,
                kind,
                box: { x: 0.04, y: 0.88, w: 0.6, h: 0.08 },
                text: "@suamarca",
                color: "#ffffff",
                background: "#111827",
            };
        default:
            return { ...base, kind: "logo", box: { x: 0.78, y: 0.04, w: 0.18, h: 0.14 }, opacity: 1 };
    }
}
function layerKind(value) {
    return value === "text" || value === "badge" || value === "contacts" || value === "logo"
        ? value
        : null;
}
function normalizeLayer(raw, index) {
    const r = asRecord(raw);
    const kind = layerKind(r.kind);
    if (!kind)
        return null;
    const id = typeof r.id === "string" && LAYER_ID_RE.test(r.id) ? r.id : `camada-${index + 1}`;
    const d = createWatermarkLayer(kind, id);
    const box = normalizeBox(r.box, d.box);
    const rotation = clamp(num(r.rotation, 0), -MAX_ROTATION, MAX_ROTATION);
    switch (d.kind) {
        case "text":
            return {
                ...d,
                box,
                rotation,
                text: cleanText(r.text, d.text),
                color: color(r.color, d.color),
                background: optionalColor(r.background, d.background),
                align: oneOf(r.align, ALIGNS, d.align),
                bold: typeof r.bold === "boolean" ? r.bold : d.bold,
            };
        case "badge":
            return {
                ...d,
                box,
                rotation,
                text: cleanText(r.text, d.text),
                color: color(r.color, d.color),
                textColor: color(r.textColor, d.textColor),
                shape: oneOf(r.shape, BADGE_SHAPES, d.shape),
            };
        case "contacts":
            return {
                ...d,
                box,
                rotation,
                text: cleanText(r.text, d.text),
                color: color(r.color, d.color),
                background: optionalColor(r.background, d.background),
            };
        default:
            return {
                ...d,
                box,
                rotation,
                opacity: clamp(num(r.opacity, d.opacity), MIN_LOGO_OPACITY, 1),
            };
    }
}
/** Ids unicos: o editor seleciona camada por id. Duplicado ganha sufixo. */
function uniqueIds(layers) {
    const seen = new Set();
    return layers.map((layer) => {
        let id = layer.id;
        for (let n = 1; seen.has(id); n++) {
            id = `${layer.id.slice(0, 18)}-${n}`;
        }
        seen.add(id);
        return id === layer.id ? layer : { ...layer, id };
    });
}
function presetId(value) {
    return typeof value === "string" && findWatermarkPreset(value)
        ? value
        : DEFAULT_WATERMARK_PRESET;
}
/** Design valido a partir de qualquer entrada (jsonb do banco, estado do editor). */
export function normalizeWatermarkDesign(raw) {
    const r = asRecord(raw);
    const preset = presetId(r.preset);
    const base = presetDesign(preset);
    const rawLayers = Array.isArray(r.layers) ? r.layers : base.layers;
    const layers = rawLayers
        .slice(0, MAX_WATERMARK_LAYERS)
        .map((layer, index) => normalizeLayer(layer, index))
        .filter((layer) => layer !== null);
    return {
        preset,
        topBand: normalizeBand(r.topBand, base.topBand),
        bottomBand: normalizeBand(r.bottomBand, base.bottomBand),
        photo: normalizePhoto(r.photo, base.photo),
        layers: uniqueIds(layers),
    };
}
function normalizeFlows(value) {
    if (!Array.isArray(value))
        return [];
    return WATERMARK_FLOWS.filter((flow) => value.includes(flow));
}
/** Linha de watermark_settings (snake_case do banco) para o modelo. */
export function normalizeWatermarkSettings(raw) {
    const r = asRecord(raw);
    return {
        enabled: r.enabled === true,
        applyTo: r.apply_to === "selected" ? "selected" : "all",
        flows: normalizeFlows(r.flows),
        design: normalizeWatermarkDesign(r.design),
        logoPath: typeof r.logo_path === "string" && r.logo_path.length > 0 ? r.logo_path : null,
        version: Math.max(0, Math.trunc(num(r.version, 0))),
    };
}
/** A marca d'agua vale para este fluxo? (liga/desliga global + onde aplicar). */
export function shouldApplyWatermark(settings, flow) {
    if (!settings || !settings.enabled)
        return false;
    return settings.applyTo === "all" || settings.flows.includes(flow);
}
//# sourceMappingURL=normalize.js.map